/**
 * Service Selection Utilities
 * 
 * This utility provides functions to handle service selection events
 * and process service responses into field structures.
 */

import { 
  createFieldStructure, 
  processFieldsIntoStructure,
  storeFieldStructure,
  setCurrentContext
} from './fieldStructureUtils';
import { CONTEXT_TYPES } from './fieldContextUtils';
import { isDocumentField, getFieldCategory } from './fieldPatternUtils';
import AppService from '../Configurations/Appservice';

/**
 * Processes a service selection event and loads fields
 * 
 * @param {Object} event - Service selection event
 * @param {Function} setFieldValue - Formik setFieldValue function
 * @param {Function} setFields - State setter for fields
 * @param {Function} setStaticFields - State setter for static fields
 * @param {Function} setAdditionalInfoRequired - State setter for additional info fields
 * @param {Function} setDocumentRequired - State setter for document fields
 * @param {Function} setChargeMapping - State setter for charge mapping
 * @param {Array} services - Available services
 * @param {Function} dispatch - Redux dispatch function
 * @returns {Promise} - Promise that resolves when fields are loaded
 */
export const handleServiceSelection = (
  event,
  setFieldValue,
  setFields,
  setStaticFields,
  setAdditionalInfoRequired,
  setDocumentRequired,
  setChargeMapping,
  services,
  dispatch
) => {
  // Set service ID in form
  setFieldValue("serviceId", event.target.value);
  
  // Create params for API call
  const params = { serviceId: event.target.value };
  
  // Reset context to DEFAULT when service changes
  setCurrentContext(CONTEXT_TYPES.DEFAULT);
  
  // Return promise for chaining
  return AppService.get("v1/serviceField/byService", { params })
    .then((response) => {
      console.log("Service fields response:", response);
      
      // Create new field structure from service response
      const serviceId = event.target.value;
      const newStructure = createFieldStructure(serviceId);
      
      // Process service fields
      const preparedServiceFields = processServiceFields(response.serviceFields);
      
      // Process static fields
      const staticFieldsArray = processStaticFields(response.staticFields);
      
      // Combine fields and remove duplicates
      const { allFields, uniqueServiceFields } = combineFieldsWithoutDuplicates(
        preparedServiceFields, 
        staticFieldsArray
      );
      
      // Store the field structure
      const fieldStructure = processFieldsIntoStructure(newStructure, allFields);
      storeFieldStructure(fieldStructure);
      
      // Set fields in component state
      setFields(allFields);
      
      // Set static fields
      setStaticFields(response.staticFields);
      
      // Set additional info and document fields (for backward compatibility)
      const additionalInfoRequired = uniqueServiceFields.filter((item) => item.type === "TEXT");
      const documentRequired = uniqueServiceFields.filter((item) => item.type !== "TEXT");
      setAdditionalInfoRequired(additionalInfoRequired);
      setDocumentRequired(documentRequired);
      
      // Set charge mapping
      const selectedService = services.find((service) => String(service.id) === String(event.target.value));
      setChargeMapping(selectedService);
      
      return { fieldStructure, allFields };
    });
};

/**
 * Process service fields from API response
 * 
 * @param {Array} serviceFields - Service fields from API
 * @returns {Array} - Processed service fields
 */
const processServiceFields = (serviceFields) => {
  if (!serviceFields || !Array.isArray(serviceFields)) {
    console.log('No service fields provided or not an array:', serviceFields);
    return [];
  }
  
  console.log('%c=== PROCESSING SERVICE FIELDS ===', 'background: #2196f3; color: white; font-weight: bold; padding: 4px 8px;');
  console.log('Raw service fields from API:', serviceFields);
  console.log('Number of service fields:', serviceFields.length);
  
  const processedFields = serviceFields.map(field => {
    // Determine category based on field name or type
    let category = getFieldCategory(field);
    
    // Create a user-friendly label prioritizing title over name/id
    let fieldLabel = field.title || field.label;
    if (!fieldLabel) {
      // If no title or label, check if name/id is a UUID and create a friendly name
      const nameToUse = field.name || field.id;
      if (nameToUse && typeof nameToUse === 'string') {
        const uuidPattern = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
        if (uuidPattern.test(nameToUse)) {
          fieldLabel = field.code || createFieldLabel(field.code) || `Service Field`;
        } else {
          fieldLabel = createFieldLabel(nameToUse);
        }
      } else {
        fieldLabel = 'Service Field';
      }
    }
    
    return {
      ...field,
      // Add attributes needed for categorization
      name: field.name || field.id,
      label: fieldLabel,
      title: fieldLabel, // Ensure title is also set for consistency
      description: field.description || '',
      // Set explicit category to ensure proper categorization
      category: category,
      // Keep any existing values if re-selecting a service
      value: field.value || null,
      // Flag to indicate this is a service field
      isServiceField: true
    };
  });
  
  console.log('%c=== PROCESSED SERVICE FIELDS RESULT ===', 'background: #4caf50; color: white; font-weight: bold; padding: 4px 8px;');
  console.log('Processed fields:', processedFields);
  console.log('Number of processed fields:', processedFields.length);
  
  return processedFields;
};

/**
 * Process static fields from API response
 * 
 * @param {Array} staticFields - Static fields from API
 * @returns {Array} - Processed static fields
 */
const processStaticFields = (staticFields) => {
  if (!staticFields || !Array.isArray(staticFields)) {
    return [];
  }
  
  // Define priority for UI display (most important fields first)
  const priorityFields = [
    'firstName', 'firstNameEn', 'lastName', 'lastNameEn', 
    'email', 'phoneNumber', 'mobileNumber',
    'address',
    'birthCertificate', 'passportCopy', 'photo', 'signature'
  ];
  
  // 1. First, process high-priority fields to ensure they appear first
  const highPriorityFields = staticFields.filter(fieldName => {
    return priorityFields.some(priorityField => 
      fieldName.toLowerCase().includes(priorityField.toLowerCase())
    );
  });
  
  // 2. Then add any remaining fields that seem useful
  const otherUsefulFields = staticFields.filter(fieldName => {
    // Skip high priority fields we've already added
    if (highPriorityFields.includes(fieldName)) return false;
    
    // Check if this field matches any known patterns
    return isFieldNameUseful(fieldName);
  });
  
  // Combine both sets with priority fields first
  const filteredStaticFields = [...highPriorityFields, ...otherUsefulFields];
  
  // Process each field
  return filteredStaticFields.map(fieldName => {
    // Determine field type - default to TEXT
    let fieldType = 'TEXT';
    
    // Check if this is a document field
    if (isDocumentField(fieldName)) {
      fieldType = 'FILE';
    }
    
    // Determine category based on field name
    const category = getFieldCategory(fieldName);
    
    // Generate a user-friendly label
    const fieldLabel = createFieldLabel(fieldName);
    
    return {
      id: fieldName,
      name: fieldName,
      label: fieldLabel,
      type: fieldType,
      category: category,
      isStatic: true,
      required: true,
      value: null
    };
  });
};

/**
 * Combine service fields and static fields without duplicates
 * 
 * @param {Array} serviceFields - Processed service fields
 * @param {Array} staticFields - Processed static fields
 * @returns {Object} - Combined fields and unique service fields
 */
const combineFieldsWithoutDuplicates = (serviceFields, staticFields) => {
  const allFieldNames = new Set();
  const uniqueServiceFields = [];
  
  // First add service fields
  for (const field of serviceFields) {
    if (field && field.name) {
      allFieldNames.add(field.name.toLowerCase());
      uniqueServiceFields.push(field);
    }
  }
  
  // Then add static fields that don't duplicate service fields
  const uniqueStaticFields = staticFields.filter(field => {
    if (!field || !field.name) return false;
    
    const fieldNameLower = field.name.toLowerCase();
    // Skip if we already have this field
    if (allFieldNames.has(fieldNameLower)) return false;
    
    // Add to our tracking set and keep this field
    allFieldNames.add(fieldNameLower);
    return true;
  });
  
  // Combine all fields
  const allFields = [...uniqueServiceFields, ...uniqueStaticFields];
  
  return { allFields, uniqueServiceFields };
};

/**
 * Check if a field name is useful based on patterns
 * 
 * @param {String} fieldName - Field name to check
 * @returns {Boolean} - True if field name is useful
 */
const isFieldNameUseful = (fieldName) => {
  if (!fieldName) return false;
  
  const lowerFieldName = fieldName.toLowerCase();
  
  // Common useful field patterns
  const usefulPatterns = [
    'name', 'first', 'last', 'middle', 'email', 'phone', 'mobile',
    'address', 'city', 'state', 'country', 'zip', 'postal',
    'birth', 'gender', 'nationality', 'passport', 'id', 'document',
    'file', 'photo', 'certificate', 'contract', 'visa',
    'location', 'medical'
  ];
  
  return usefulPatterns.some(pattern => lowerFieldName.includes(pattern));
};

/**
 * Create a user-friendly label from a field name
 * 
 * @param {String} fieldName - Field name to convert
 * @returns {String} - User-friendly label
 */
const createFieldLabel = (fieldName) => {
  if (!fieldName) return '';
  
  return fieldName
    .replace(/([A-Z])/g, ' $1') // Split camelCase
    .replace(/^./, str => str.toUpperCase()) // Capitalize first letter
    .replace(/Ar$/, ' (Arabic)') // Add Arabic indicator if field ends with Ar
    .replace(/En$/, ' (English)'); // Add English indicator if field ends with En
};
